<template>
  <a :href="url" class="navigation-banner" :class="classes">
    <Media
      class="media"
      :images="enhancedImages"
      :cover="cover || useCoverForImageAlign"
      :cover-position="coverPosition"
      eager
    />
    <div class="content">
      <div v-if="meta" class="meta">
        <div v-if="meta.left" class="meta__left">{{ meta.left }}</div>
        <div v-if="meta.right" class="meta__right">{{ meta.right }}</div>
      </div>
      <Button tag="a" :href="url" form="underline-invert" class="title">
        {{ text }}
      </Button>
    </div>
  </a>
</template>

<script>
import Button from "@/components/ButtonV2.vue";
import Media from "@/components/Media.vue";
import { enhanceNavigationBannerImages } from "@/helpers/navigationBannerImages.js";

export default {
  name: "NavigationBanner",
  components: {
    Media,
    Button,
  },
  props: {
    images: {
      type: Object,
      required: true,
    },
    text: {
      type: String,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    meta: {
      type: Object,
      default: null,
    },
    theme: {
      type: String,
      default: "primary",
      validator: (value) => {
        return ["primary", "highlight"].includes(value);
      },
    },
    orientation: {
      type: String,
      default: "portrait",
      validator: (value) => {
        return ["portrait", "landscape"].includes(value);
      },
    },
    /**
     * When true, image uses object-fit cover and on desktop (md+) is anchored to the bottom
     * so portrait crops show the lower portion of the asset (e.g. faces lower in frame).
     * Mobile keeps top anchoring.
     */
    alignImageBottomDesktop: {
      type: Boolean,
      default: false,
    },
    cover: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    enhancedImages() {
      return enhanceNavigationBannerImages(this.images);
    },
    useCoverForImageAlign() {
      return this.alignImageBottomDesktop;
    },
    coverPosition() {
      return this.alignImageBottomDesktop ? "center bottom" : "center top";
    },
    classes() {
      return {
        "navigation-banner--highlight": this.theme === "highlight",
        "navigation-banner--landscape": this.orientation === "landscape",
        "navigation-banner--portrait": this.orientation === "portrait",
        "navigation-banner--fixed-media": this.cover,
        "navigation-banner--align-bottom-desktop": this.alignImageBottomDesktop,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.navigation-banner {
  text-decoration: none;

  .media {
    aspect-ratio: auto;
  }

  &--highlight .title {
    color: $color-red;
  }

  &--landscape {
    .media {
      aspect-ratio: 19/11.9;
    }
  }

  &--portrait {
    .media {
      aspect-ratio: 4/5;
    }
  }

  &--fixed-media {
    &.navigation-banner--landscape .media,
    &.navigation-banner--portrait .media {
      aspect-ratio: auto;
      max-height: 282px;
    }
  }
}

.media {
  margin-bottom: 12px;
  overflow: hidden;
}

/* Desktop-only: anchor image to bottom when alignImageBottomDesktop is set (requires cover mode) */
.navigation-banner--align-bottom-desktop {
  :deep(.media.media--cover img),
  :deep(.media.media--cover video) {
    @include respond-above(md) {
      object-position: center bottom;
    }
  }
}

.content {
  padding: 0 24px 20px;
  text-align: center;

  @include respond-above(md) {
    padding: 0;
  }
}

.meta {
  @include utility-label(12px);
  color: $color-grey-dark;
  margin-bottom: 2px;
  display: flex;
  justify-content: space-between;
}

.title :deep(.text) {
  display: inline-block;
  max-width: 100%;
  text-align: center;
  @include editorial-headline(18px);
  font-style: italic;
  text-transform: none;
}
</style>
